import { Chip, type Column, Modal } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { UnauthorizedView } from "../../../components/UnauthorizedView/index.js";
import { ROUTES } from "../../../routes.js";
import { useQuery } from "../../../hooks/useApi.js";
import {
  platformService,
  type ServiceEntry,
  type ServicesResponse,
  type ServiceInstance,
} from "../../../api/platform.service.js";
import { useSSEStream } from "../../../hooks/useSSEStream.js";
import styles from "./Services.module.css";

const FALLBACK_REFRESH_INTERVAL = 30000;

function getAggregateStatus(instances: ServiceInstance[]): string {
  if (instances.length === 0) return "unknown";
  const reachable = instances.filter(i => i.status !== "unreachable");
  if (reachable.length === 0) return "unreachable";
  if (reachable.some(i => i.status === "unhealthy")) return "unhealthy";
  if (reachable.some(i => i.status === "degraded")) return "degraded";
  return "healthy";
}

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    healthy: styles.statusHealthy,
    degraded: styles.statusDegraded,
    unhealthy: styles.statusUnhealthy,
    unreachable: styles.statusUnreachable,
    unknown: styles.statusUnknown,
  };

  return (
    <Chip size="small" variant="filled" className={colorMap[status] || styles.statusUnknown}>
      {status.toUpperCase()}
    </Chip>
  );
}

function formatRelativeTime(dateString: string, nowMs: number): string {
  const diff = nowMs - new Date(dateString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

function formatDateTime(dateString: string): string {
  const d = new Date(dateString);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ServicesPage() {
  const { data, loading, error, refetch } = useQuery(() => platformService.getServices(), []);
  const [streamData, setStreamData] = useState<ServicesResponse | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);

  const [selectedService, setSelectedService] = useState<ServiceEntry | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "instances" | "routes" | "featureFlags" | "notifications"
  >("instances");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const effectiveData = streamData ?? data;
  const handleStreamData = useCallback((payload: ServicesResponse) => {
    setStreamError(null);
    setStreamData(payload);
  }, []);
  const handleStreamError = useCallback((streamErr: Error) => {
    setStreamError(streamErr.message);
  }, []);

  const { connected: streamConnected } = useSSEStream<ServicesResponse>({
    url: platformService.getServicesStreamUrl(),
    enabled: true,
    isDataEvent: (payload: unknown): payload is ServicesResponse =>
      !!payload &&
      typeof payload === "object" &&
      Array.isArray((payload as { services?: unknown }).services),
    onData: handleStreamData,
    onError: handleStreamError,
  });

  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (streamConnected) return;

    const interval = setInterval(() => refetch(), FALLBACK_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refetch, streamConnected]);

  useEffect(() => {
    if (!selectedService || !effectiveData?.services) return;
    const updated = effectiveData.services.find(service => service.name === selectedService.name);
    if (updated) {
      setSelectedService(updated);
    }
  }, [effectiveData, selectedService]);

  const handleRowClick = useCallback((service: ServiceEntry) => {
    setSelectedService(service);
    setActiveTab("instances");
    setIsModalOpen(true);
  }, []);

  const columns: Column<ServiceEntry>[] = useMemo(
    () => [
      {
        header: "Service",
        accessor: "name" as keyof ServiceEntry,
        render: (value: unknown) => <strong>{value as string}</strong>,
      },
      {
        header: "Status",
        accessor: "instances" as keyof ServiceEntry,
        render: (_value: unknown, row: ServiceEntry) => (
          <StatusBadge status={getAggregateStatus(row.instances)} />
        ),
      },
      {
        header: "Instances",
        accessor: "instances" as keyof ServiceEntry,
        render: (value: unknown) => String((value as ServiceInstance[]).length),
      },
      {
        header: "Version",
        accessor: "instances" as keyof ServiceEntry,
        render: (value: unknown) => {
          const instances = value as ServiceInstance[];
          const versions = [...new Set(instances.map(i => i.version))];
          return versions.join(", ") || "-";
        },
      },
      {
        header: "Routes",
        accessor: "routes" as keyof ServiceEntry,
        render: (value: unknown) => String((value as unknown[]).length),
      },
      {
        header: "Feature Flags",
        accessor: "featureFlags" as keyof ServiceEntry,
        render: (value: unknown) => String((value as unknown[]).length),
      },
      {
        header: "Notifications",
        accessor: "notifications" as keyof ServiceEntry,
        render: (value: unknown) => String(((value as unknown[] | undefined) ?? []).length),
      },
      {
        header: "First Heartbeat",
        accessor: "firstHeartbeat" as keyof ServiceEntry,
        render: (value: unknown) => {
          const val = value as string | null;
          return val ? formatDateTime(val) : "—";
        },
      },
      {
        header: "Last Heartbeat",
        accessor: "lastUpdated" as keyof ServiceEntry,
        render: (value: unknown) => formatRelativeTime(value as string, nowMs),
      },
    ],
    [nowMs],
  );

  if (error) {
    const status = (error as Error & { status?: number }).status;
    if (status === 401 || status === 403) return <UnauthorizedView />;
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Services" subtitle="Discovered services and their health status" />
      </div>

      {loading && <LoadingState message="Loading services..." />}

      {error && !loading && (
        <EmptyState
          variant="error"
          title="Failed to load services"
          message={error.message}
          action={{ label: "Retry", onClick: () => refetch() }}
        />
      )}

      {!loading && !error && (
        <div className={styles.content}>
          {!effectiveData?.services?.length ? (
            <EmptyState
              variant="empty"
              title="No services discovered"
              message="No services are currently broadcasting their presence via Redis pub/sub."
            />
          ) : (
            <DataTable
              data={effectiveData.services}
              columns={columns}
              onRowClick={handleRowClick}
              rowKey="name"
              size="small"
            />
          )}
        </div>
      )}
      {!loading && !error && streamError && (
        <div className={styles.streamWarning}>
          Live stream disconnected, using fallback refresh.
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedService?.name ?? ""}
        size="large"
        blurBackground
      >
        {selectedService && (
          <div className={styles.modalContent}>
            <div className={styles.tabButtons}>
              <button
                type="button"
                className={`${styles.tabButton} ${activeTab === "instances" ? styles.tabButtonActive : ""}`}
                onClick={() => setActiveTab("instances")}
              >
                Instances ({selectedService.instances.length})
              </button>
              <button
                type="button"
                className={`${styles.tabButton} ${activeTab === "routes" ? styles.tabButtonActive : ""}`}
                onClick={() => setActiveTab("routes")}
              >
                Routes ({selectedService.routes.length})
              </button>
              <button
                type="button"
                className={`${styles.tabButton} ${activeTab === "featureFlags" ? styles.tabButtonActive : ""}`}
                onClick={() => setActiveTab("featureFlags")}
              >
                Feature Flags ({selectedService.featureFlags.length})
              </button>
              <button
                type="button"
                className={`${styles.tabButton} ${activeTab === "notifications" ? styles.tabButtonActive : ""}`}
                onClick={() => setActiveTab("notifications")}
              >
                Notification Capabilities ({selectedService.notifications?.length ?? 0})
              </button>
            </div>

            {activeTab === "instances" && (
              <>
                <h3 className={styles.sectionTitle}>Instances</h3>
                <table className={styles.detailTable}>
                  <thead>
                    <tr>
                      <th>Instance ID</th>
                      <th>Version</th>
                      <th>Status</th>
                      <th>URL</th>
                      <th>First Heartbeat</th>
                      <th>Last Heartbeat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedService.instances.map(inst => (
                      <tr key={inst.instanceId}>
                        <td className={styles.mono}>{inst.instanceId}</td>
                        <td>{inst.version}</td>
                        <td>
                          <StatusBadge status={inst.status} />
                        </td>
                        <td className={styles.mono}>{inst.url}</td>
                        <td>{formatDateTime(inst.firstHeartbeat)}</td>
                        <td>{formatRelativeTime(inst.lastHeartbeat, nowMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {activeTab === "routes" && (
              <>
                <h3 className={styles.sectionTitle}>Routes</h3>
                <table className={styles.detailTable}>
                  <thead>
                    <tr>
                      <th>Method</th>
                      <th>Path</th>
                      <th>Permissions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedService.routes.map((route, i) => (
                      <tr key={`${route.method}-${route.path}-${i}`}>
                        <td>
                          <span className={styles.methodBadge}>{route.method}</span>
                        </td>
                        <td className={styles.mono}>{route.path}</td>
                        <td>{route.permissions.join(", ") || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {activeTab === "featureFlags" && (
              <>
                <h3 className={styles.sectionTitle}>Feature Flags</h3>
                <table className={styles.detailTable}>
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Scopes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedService.featureFlags.map(flag => (
                      <tr key={flag.key}>
                        <td className={styles.mono}>
                          <Link to={ROUTES.developer.featureFlags} className={styles.flagLink}>
                            {flag.key}
                          </Link>
                        </td>
                        <td>{flag.name}</td>
                        <td>
                          <span className={styles.typeBadge}>{flag.flagType}</span>
                        </td>
                        <td>
                          <div className={styles.scopeChips}>
                            {flag.scopes.map(scope => (
                              <Chip key={scope} size="small" variant="filled">
                                {scope}
                              </Chip>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {activeTab === "notifications" && (
              <>
                <h3 className={styles.sectionTitle}>Notification Capabilities</h3>
                {(selectedService.notifications?.length ?? 0) === 0 ? (
                  <p>No notification capabilities declared by this service.</p>
                ) : (
                  <table className={styles.detailTable}>
                    <thead>
                      <tr>
                        <th>Stream</th>
                        <th>Actions</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedService.notifications.map(notification => (
                        <tr key={notification.stream}>
                          <td className={styles.mono}>{notification.stream}</td>
                          <td className={styles.mono}>
                            {notification.actions.map(a => a.action).join(", ")}
                          </td>
                          <td>{notification.description || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default ServicesPage;
